import React, { useState } from 'react';

import icon from '@assets/img/icons/profile-settings.svg';
import { EmailDuplicateDialog } from '@components/admin/members/EmailDuplicateDialog';
import { Button } from '@components/Button';
import { LogForm } from '@components/Form';
import { FormAlert } from '@components/Forms/FormAlert';
import { TextInput, TextInputProps } from '@components/Forms/TextInput';
import { HorizontalSpace } from '@components/HorizontalSpace';
import { Layout } from '@components/Layout';
import { AccountManagerContainer } from '@components/settings/AccountManagerContainer';
import { EmailChangePromptDialog } from '@components/settings/EmailChangePromptDialog';
import { SpinnerModal } from '@components/SpinnerModal';
import { usePatchUser } from '@hooks/mutation/usePatchUser';
import { useUser } from '@hooks/queries/useUser';
import { AccountManagerForm } from '@models/AccountManagerForm';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { EmailDuplicateError } from '@utils/errors';
import { createValidate, Field } from '@utils/forms';
import { createStrings } from '@utils/local';
import { PageMeta } from '@utils/pages';
import { Form, Formik } from 'formik';

const strings = createStrings({
  ja: {
    account: 'アカウント設定',
    title: 'プロフィール',
    update: '変更',
    nameIsChanged: '個人情報が変更されました',
    updateProfile: '個人情報を変更する',
    confirmChangeEmail: (email: string) => (
      <>
        メールアドレスを<span style={{ color: '#CF3F1B' }}>{email}</span>に変更されますか？
      </>
    ),
    afterChangeEmail:
      '「OK」をクリックすると現在のメールアドレスではD2Dにログインできなくなりますので、ご注意ください。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  formContainer: {
    flex: 1,
    backgroundColor: theme.palette.background.paper,
    padding: '30px 1rem',
  },
  formInner: {
    ...common.flexColumn,
    ...common.verticalSpace1,
    maxWidth: '30rem',
    margin: '0 auto',
  },
  prompt: {
    ...common.normalText,
    paddingBottom: '2rem',
  },
  inlineForm: {
    flex: 1,
  },
  firstNameLastNameContainer: {
    ...common.flexInline,
    alignItems: 'flex-end',
  },
  firstNameLastNameDivider: {
    width: 10,
    height: 1,
    backgroundColor: theme.palette.grey['500'],
    margin: '0 10px 22px 10px',
  },
  privatePolicy: {
    ...common.middleText,
    color: theme.palette.grey['500'],
  },
  privatePolicyLink: {
    color: theme.palette.primary.main,
  },
  title: {
    ...common.normalText,
    ...common.flexInline,
    ...common.fontWeight700,
    padding: '17px 10px',
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    alignItems: 'center',
  },
  body: {
    ...common.flexColumn,
    padding: '40px 30px',
    flexShrink: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  actions: {
    ...common.flexInline,
    justifyContent: 'center',
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    padding: 32,
    columnGap: 64,
  },
  paper: {
    maxWidth: '700px',
  },
}));

const schema = AccountManagerForm.shared.toProfileUpdateSchema();

export const Profile: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const user = useUser();
  const patchUser = usePatchUser();
  const [openEmailChangePromptDialog, setOpenEmailChangePromptDialog] = useState(false);
  const [openEmailDuplicateDialog, setOpenEmailDuplicateDialog] = useState(false);

  const initialValues = {
    lastName: user.data?.last_name ?? '',
    firstName: user.data?.first_name ?? '',
    email: user.data?.email ?? '',
  };

  const renderTextField = (field: Field<any>, props?: Partial<TextInputProps>) => {
    return (
      <TextInput
        label={field.label}
        name={field.name}
        itemVariant="column"
        itemErrorVariant="inline"
        {...props}
      />
    );
  };

  const renderEmailChangePromptDialog = (values: {
    lastName: string;
    firstName: string;
    email: string;
  }) => (
    <EmailChangePromptDialog
      onClose={() => setOpenEmailChangePromptDialog(false)}
      open={openEmailChangePromptDialog}
      title={strings.updateProfile}
      email={values.email}
      onConfirm={async () => {
        try {
          await patchUser.mutateAsync({
            last_name: values.lastName,
            first_name: values.firstName,
            email: values.email,
          });
        } catch (e) {
          if (e instanceof EmailDuplicateError) {
            setOpenEmailChangePromptDialog(false);
            setOpenEmailDuplicateDialog(true);
          }
        }
        setOpenEmailChangePromptDialog(false);
      }}
    />
  );

  return (
    <Layout
      loading={user.isLoading}
      title={strings.title}
      breadcrumbs={[
        '/utilities/Dashboard',
        { text: strings.account },
        '/settings/AccountManager/Profile',
      ]}
    >
      {() => (
        <AccountManagerContainer>
          <Formik
            initialValues={initialValues}
            validate={createValidate(schema)}
            onSubmit={(values) => {
              if (values.email === initialValues.email) {
                patchUser.mutateAsync({
                  last_name: values.lastName,
                  first_name: values.firstName,
                });
              } else {
                setOpenEmailChangePromptDialog(true);
              }
            }}
          >
            {({ values }) => (
              <Form data-testid="AccountProfile" className={classes.formContainer}>
                {renderEmailChangePromptDialog(values)}
                <EmailDuplicateDialog
                  open={openEmailDuplicateDialog}
                  onClose={() => setOpenEmailDuplicateDialog(false)}
                />
                <SpinnerModal open={patchUser.isLoading} />
                <LogForm name="profile-settings" disabled />
                <div className={classes.formInner}>
                  <FormAlert
                    mutation={patchUser}
                    showSuccess
                    successMessage={strings.nameIsChanged}
                  />
                  <div className={classes.flexInline}>
                    {renderTextField(AccountManagerForm.shared.lastName, {
                      formClasses: { root: classes.flex1 },
                    })}
                    <HorizontalSpace width={39} />
                    {renderTextField(AccountManagerForm.shared.firstName, {
                      formClasses: { root: classes.flex1 },
                    })}
                  </div>
                  {renderTextField(AccountManagerForm.shared.email, {
                    type: 'email',
                    autoComplete: 'new-email',
                  })}

                  <div />
                  <Button text={strings.update} color="primary" />
                </div>
              </Form>
            )}
          </Formik>
        </AccountManagerContainer>
      )}
    </Layout>
  );
};

export const meta: PageMeta = {
  title: strings.title,
  icon,
};
